import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  PieChart, Target, List, Globe, Search, ExternalLink,
  X, Copy, Zap, TrendingUp, TrendingDown, Mail, CheckCircle,
  Sparkles, Loader2, Link2, CircleCheck, CircleDashed,
} from 'lucide-react'
import * as React from 'react'
import { Link } from 'react-router-dom'

import { FaviconImg } from '../../../components/dashboard/FaviconImg'
import { useActiveWebsite } from '../../../hooks/use-active-website'
import { api, ApiError } from '../../../lib/api'
import { SOURCE_TYPE_COLORS, SOURCE_TYPE_DESCRIPTIONS, getSourceTypeBadgeClass } from '../../../lib/source-types'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CitationDomain {
  domain: string
  times_cited: number
  times_mentioned: number
  unique_prompts: number
  last_seen: string
  urls: string[]
  type: string
}

type MentionFilter = 'all' | 'mentioned' | 'not_mentioned'

interface SourcesData {
  domains: CitationDomain[]
  competitor_domains: string[]
}

interface CitationBrief {
  id: string
  domain: string
  brief: string
  outreach_email: string
  steps: string[]
  created_at: string
}

type PageTab = 'landscape' | 'gaps' | 'feed'
type BriefTab = 'brief' | 'email' | 'steps'

// ─── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

function isThisWeek(dateStr: string): boolean {
  return Date.now() - new Date(dateStr).getTime() < 7 * 24 * 60 * 60 * 1000
}

const BRIEF_GRADIENT = 'linear-gradient(135deg, #ec4899, #8b5cf6)'

// ─── TypeBadge ─────────────────────────────────────────────────────────────────

function TypeBadge({ type }: { type: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[9px] font-semibold ${getSourceTypeBadgeClass(type)}`}>
      {type}
    </span>
  )
}

// ─── MentionBadge ──────────────────────────────────────────────────────────────
// Per-domain indicator: was the brand actually mentioned in the AI response(s)
// that cited this domain, or did the citation happen without a mention?

function MentionBadge({ timesMentioned, timesCited }: { timesMentioned: number; timesCited: number }) {
  const mentioned = timesMentioned > 0
  const title = mentioned
    ? `Your brand was mentioned in ${timesMentioned} of the ${timesCited} AI response${timesCited !== 1 ? 's' : ''} that cited this domain`
    : `Your brand was not mentioned in any of the ${timesCited} AI response${timesCited !== 1 ? 's' : ''} that cited this domain`
  return (
    <span
      title={title}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-semibold ${
        mentioned
          ? 'border-feedback-success/25 bg-feedback-success/10 text-feedback-success'
          : 'border-border text-text-muted'
      }`}
    >
      {mentioned
        ? <CircleCheck className="h-2.5 w-2.5" />
        : <CircleDashed className="h-2.5 w-2.5" />
      }
      {mentioned ? `Mentioned ${timesMentioned}/${timesCited}` : 'Not mentioned'}
    </span>
  )
}

// ─── MentionFilterToggle ───────────────────────────────────────────────────────

function MentionFilterToggle({ value, onChange, counts }: {
  value: MentionFilter
  onChange: (v: MentionFilter) => void
  counts: { all: number; mentioned: number; not_mentioned: number }
}) {
  const OPTIONS: { id: MentionFilter; label: string }[] = [
    { id: 'all', label: `All (${counts.all})` },
    { id: 'mentioned', label: `Mentioned (${counts.mentioned})` },
    { id: 'not_mentioned', label: `Not mentioned (${counts.not_mentioned})` },
  ]
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-[11px] font-medium text-text-muted">Filter:</span>
      {OPTIONS.map(({ id, label }) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            value === id
              ? 'border-brand-accent/40 bg-brand-accent/10 text-brand-accent'
              : 'border-border text-text-muted hover:bg-surface-overlay'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

// ─── DonutChart ────────────────────────────────────────────────────────────────

function DonutChart({ counts, total }: { counts: Record<string, number>; total: number }) {
  const R = 48
  const C = 2 * Math.PI * R
  const cx = 60
  const cy = 60
  const gap = total > 0 ? 2 : 0

  const segments: { type: string; color: string; dash: number; offset: number }[] = []
  let cumulative = 0
  for (const [type, count] of Object.entries(counts)) {
    if (count === 0) continue
    const frac = count / total
    segments.push({
      type,
      color: SOURCE_TYPE_COLORS[type as keyof typeof SOURCE_TYPE_COLORS] ?? '#6366f1',
      dash: Math.max(0, frac * C - gap),
      offset: C - cumulative * C,
    })
    cumulative += frac
  }

  return (
    <svg width={120} height={120} viewBox="0 0 120 120">
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="#e2e8f0" strokeWidth={12} />
      {total === 0
        ? <circle cx={cx} cy={cy} r={R} fill="none" stroke="#e2e8f0" strokeWidth={12} />
        : segments.map((s) => (
          <circle
            key={s.type}
            cx={cx} cy={cy} r={R}
            fill="none"
            stroke={s.color}
            strokeWidth={12}
            strokeDasharray={`${s.dash} ${C}`}
            strokeDashoffset={s.offset}
            strokeLinecap="butt"
            style={{ transform: 'rotate(-90deg)', transformOrigin: `${cx}px ${cy}px` }}
          />
        ))
      }
      <text x={cx} y={cy - 5} textAnchor="middle" fill="#0f172a" fontSize={18} fontWeight={700}>{total}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="#64748b" fontSize={9}>citations</text>
    </svg>
  )
}

// ─── TabButton ─────────────────────────────────────────────────────────────────

function TabButton({ active, onClick, icon, label }: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      onClick={onClick}
      className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-brand-accent/30 bg-brand-accent/10 text-brand-accent'
          : 'border-border bg-transparent text-text-muted hover:border-border-strong hover:text-text'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}

// ─── Landscape Tab ─────────────────────────────────────────────────────────────

function LandscapeTab({ domains }: { domains: CitationDomain[] }) {
  const totalCitations = domains.reduce((s, d) => s + d.times_cited, 0)
  const newThisWeek = domains.filter((d) => isThisWeek(d.last_seen)).length

  const counts: Record<string, number> = { Editorial: 0, UGC: 0, Competitor: 0, Reference: 0 }
  domains.forEach((d) => { counts[d.type] = (counts[d.type] ?? 0) + d.times_cited })

  const hasAnyType = Object.values(counts).some((c) => c > 0)

  return (
    <div className="flex flex-col gap-6">
      {/* Scorecard row */}
      <div className="flex flex-wrap items-center gap-8 rounded-xl border border-border bg-surface p-6">
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-text-muted">Cited Sources</p>
          <p className="bg-gradient-to-br from-brand-accent to-[#8b5cf6] bg-clip-text text-3xl font-bold text-transparent">
            {domains.length}
          </p>
          <p className="mt-0.5 text-[11px] text-text-muted">unique domains</p>
        </div>

        <div className="h-12 w-px shrink-0 bg-border" />

        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-text-muted">Total Citations</p>
          <p className="text-3xl font-bold text-text">{totalCitations}</p>
          <p className="mt-0.5 text-[11px] text-text-muted">collected by Perplexity</p>
        </div>

        <div className="h-12 w-px shrink-0 bg-border" />

        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-text-muted">This Week</p>
          <div className="flex items-center gap-1.5">
            <p className="text-3xl font-bold text-text">{newThisWeek}</p>
            {newThisWeek > 0
              ? <TrendingUp className="h-4 w-4 text-feedback-success" />
              : <TrendingDown className="h-4 w-4 text-text-muted opacity-40" />
            }
          </div>
          <p className={`mt-0.5 text-[11px] ${newThisWeek > 0 ? 'text-feedback-success' : 'text-text-muted'}`}>
            {newThisWeek > 0 ? `${newThisWeek} new citation${newThisWeek !== 1 ? 's' : ''}` : 'no new citations'}
          </p>
        </div>

        <div className="ml-auto">
          <DonutChart counts={counts} total={totalCitations} />
        </div>
      </div>

      {/* Type breakdown */}
      <div>
        <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-text-muted">Citation Types</p>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
          {(['Editorial', 'UGC', 'Reference', 'Competitor'] as const).map((type) => {
            const count = counts[type] ?? 0
            const pct = totalCitations > 0 ? Math.round((count / totalCitations) * 100) : 0
            const color = SOURCE_TYPE_COLORS[type as keyof typeof SOURCE_TYPE_COLORS] ?? '#6366f1'
            return (
              <div
                key={type}
                className="rounded-xl border border-border bg-surface p-4"
                style={{ borderLeftWidth: 3, borderLeftColor: color }}
              >
                <div className="mb-2 flex items-center justify-between">
                  <TypeBadge type={type} />
                  <span className="text-[11px] text-text-muted">{pct}%</span>
                </div>
                <p className="mb-0.5 text-2xl font-bold text-text">{count}</p>
                <p className="text-[11px] text-text-muted">{SOURCE_TYPE_DESCRIPTIONS[type] ?? ''}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Top cited domains */}
      {hasAnyType && (
        <div>
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-text-muted">Top Cited Domains</p>
          <div className="overflow-hidden rounded-xl border border-border bg-surface">
            {domains.slice(0, 10).map((d, i) => (
              <div
                key={d.domain}
                className={`flex items-center gap-3 px-4 py-3 ${i < Math.min(domains.length, 10) - 1 ? 'border-b border-border' : ''}`}
              >
                <span className="w-4 shrink-0 text-[11px] tabular-nums text-text-muted">{i + 1}</span>
                <FaviconImg domain={d.domain} name={d.domain} size={18} />
                <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-text">{d.domain}</span>
                <TypeBadge type={d.type} />
                <MentionBadge timesMentioned={d.times_mentioned} timesCited={d.times_cited} />
                <span className="min-w-[28px] text-right text-xs font-semibold text-text">{d.times_cited}×</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Citation Brief Overlay ────────────────────────────────────────────────────

function CitationBriefOverlay({
  domain,
  domainType,
  timesCited,
  prompts,
  websiteId,
  existingBrief,
  onClose,
  onBriefGenerated,
}: {
  domain: string
  domainType: string
  timesCited: number
  prompts: number
  websiteId: string | null
  existingBrief: CitationBrief | null
  onClose: () => void
  onBriefGenerated: (brief: CitationBrief) => void
}) {
  const queryClient = useQueryClient()
  const [tab, setTab] = React.useState<BriefTab>('brief')
  const [open, setOpen] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const [genError, setGenError] = React.useState<string | null>(null)

  React.useEffect(() => {
    const t = setTimeout(() => setOpen(true), 10)
    return () => clearTimeout(t)
  }, [])

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  function close() {
    setOpen(false)
    setTimeout(onClose, 280)
  }

  const generateMutation = useMutation({
    mutationFn: () => {
      if (!websiteId) throw new Error('No active website found.')
      return api.post<CitationBrief>('/api/citation-intelligence/generate', {
        domain,
        website_id: websiteId,
      })
    },
    onSuccess: (brief) => {
      queryClient.invalidateQueries({ queryKey: ['citation-intelligence'] })
      onBriefGenerated(brief)
      setTab('brief')
      setGenError(null)
    },
    onError: (err: unknown) => {
      setGenError(err instanceof ApiError ? err.message : 'Failed to generate brief — please try again')
    },
  })

  const brief = generateMutation.data ?? existingBrief

  function copyEmail() {
    if (!brief?.outreach_email) return
    navigator.clipboard.writeText(brief.outreach_email).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {})
  }

  const BRIEF_TABS: { id: BriefTab; icon: React.ReactNode; label: string }[] = [
    { id: 'brief', icon: <Sparkles className="h-3.5 w-3.5" />, label: 'Why It Matters' },
    { id: 'email', icon: <Mail className="h-3.5 w-3.5" />, label: 'Outreach Email' },
    { id: 'steps', icon: <List className="h-3.5 w-3.5" />, label: 'Action Steps' },
  ]

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end justify-center transition-[opacity,background-color] duration-200 ${open ? 'bg-black/50 opacity-100' : 'bg-black/0 opacity-0'}`}
      onClick={(e) => { if (e.target === e.currentTarget) close() }}
    >
      <div
        className={`relative flex h-[88vh] w-full max-w-[960px] flex-col overflow-hidden rounded-t-2xl border border-b-0 border-border bg-surface shadow-2xl transition-transform duration-[280ms] ease-out ${open ? 'translate-y-0' : 'translate-y-full'}`}
      >
        {/* Top accent bar */}
        <div className="absolute left-0 right-0 top-0 h-0.5 rounded-tl-2xl rounded-tr-2xl" style={{ background: BRIEF_GRADIENT }} />

        {/* Header */}
        <div className="flex shrink-0 items-center gap-4 border-b border-border px-7 py-5">
          <FaviconImg domain={domain} name={domain} size={44} className="!rounded-xl" />
          <div className="min-w-0 flex-1">
            <p className="text-[17px] font-bold text-text">{domain}</p>
            <p className="mt-0.5 flex items-center gap-2 text-xs text-text-muted">
              <TypeBadge type={domainType} />
              <span>{timesCited} citations · {prompts} prompt{prompts !== 1 ? 's' : ''}</span>
            </p>
          </div>
          <button
            onClick={close}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-overlay text-text-muted transition-colors hover:text-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tab bar — only shown when brief exists */}
        {brief && (
          <div className="flex shrink-0 gap-2 border-b border-border px-7 py-3.5">
            {BRIEF_TABS.map(({ id, icon, label }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 rounded-lg border px-4 py-2 text-[13px] font-medium transition-colors ${
                  tab === id
                    ? 'border-pink-300/40 bg-pink-50 text-pink-600'
                    : 'border-border bg-transparent text-text-muted hover:text-text'
                }`}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-7">
          {/* Generate CTA */}
          {!brief && !generateMutation.isPending && (
            <div className="flex h-full flex-col items-center justify-center">
              <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-pink-100 bg-pink-50">
                <Target className="h-8 w-8 text-pink-500" />
              </div>
              <p className="mb-2 text-center text-base font-semibold text-text">Get cited by {domain}</p>
              <p className="mb-7 max-w-[420px] text-center text-[13px] leading-relaxed text-text-muted">
                Perplexity uses <strong className="text-text">{domain}</strong> as a trusted source. Generate a personalised brief — including an outreach email and step-by-step actions — to get your brand featured there.
              </p>
              {genError && (
                <div className="mb-5 w-full max-w-sm rounded-xl border border-feedback-error/20 bg-feedback-error/5 px-4 py-3">
                  <p className="text-[13px] text-feedback-error">{genError}</p>
                </div>
              )}
              <button
                onClick={() => generateMutation.mutate()}
                disabled={generateMutation.isPending}
                className="flex items-center gap-2 rounded-xl px-7 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ background: BRIEF_GRADIENT }}
              >
                <Zap className="h-4 w-4" />
                Get Cited Here — 1 credit
              </button>
            </div>
          )}

          {/* Loading state */}
          {generateMutation.isPending && (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <Loader2 className="h-7 w-7 animate-spin text-pink-500" />
              <p className="text-[13px] text-text-muted">Researching {domain} with Perplexity…</p>
            </div>
          )}

          {/* Brief content */}
          {brief && (
            <div>
              {/* Why It Matters */}
              {tab === 'brief' && (
                <div className="flex flex-col gap-5">
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-muted">Why This Domain Matters</p>
                    <h3 className="text-[15px] font-semibold text-text">Why Perplexity cites {domain} — and how to get in</h3>
                  </div>
                  <div className="rounded-xl border border-border bg-surface-raised p-5">
                    <div className="flex gap-3">
                      <div
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                        style={{ background: BRIEF_GRADIENT }}
                      >
                        <Sparkles className="h-4 w-4 text-white" />
                      </div>
                      <p className="text-[14px] leading-relaxed text-text-muted">{brief.brief}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 rounded-xl border border-pink-200/60 bg-pink-50 px-4 py-3.5">
                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-pink-500" />
                    <p className="text-[13px] leading-relaxed text-text-muted">
                      Getting cited by <strong className="text-text">{domain}</strong> could directly increase how often Perplexity references your brand in AI responses for your target queries.
                    </p>
                  </div>
                </div>
              )}

              {/* Outreach Email */}
              {tab === 'email' && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-muted">Outreach Email</p>
                      <h3 className="text-[15px] font-semibold text-text">Ready-to-send pitch for {domain}</h3>
                    </div>
                    <button
                      onClick={copyEmail}
                      className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                        copied
                          ? 'border-feedback-success/25 bg-feedback-success/10 text-feedback-success'
                          : 'border-border bg-surface-overlay text-text-muted hover:text-text'
                      }`}
                    >
                      <Copy className="h-3 w-3" />
                      {copied ? 'Copied!' : 'Copy email'}
                    </button>
                  </div>
                  <div className="rounded-xl border border-border bg-surface-raised p-5">
                    <pre className="whitespace-pre-wrap break-words font-mono text-[13px] leading-relaxed text-text-muted">
                      {brief.outreach_email}
                    </pre>
                  </div>
                </div>
              )}

              {/* Action Steps */}
              {tab === 'steps' && (
                <div className="flex flex-col gap-4">
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-muted">Action Steps</p>
                    <h3 className="text-[15px] font-semibold text-text">How to get featured on {domain}</h3>
                  </div>
                  {(brief.steps ?? []).map((step, i) => (
                    <div key={i} className="flex gap-3.5 rounded-xl border border-border bg-surface-raised p-4">
                      <div
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
                        style={{ background: BRIEF_GRADIENT }}
                      >
                        {i + 1}
                      </div>
                      <p className="text-[13px] leading-relaxed text-text-muted">{step}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {brief && (
          <div className="flex shrink-0 items-center justify-between border-t border-border px-7 py-3">
            <span className="text-[11px] text-text-muted">
              Generated {new Date(brief.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-pink-400" />
              <span className="text-[11px] text-text-muted">Researched by Perplexity sonar</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Gap Analysis Tab ──────────────────────────────────────────────────────────

function GapAnalysisTab({
  domains,
  websiteId,
  briefs,
  onBriefGenerated,
}: {
  domains: CitationDomain[]
  websiteId: string | null
  briefs: Record<string, CitationBrief>
  onBriefGenerated: (brief: CitationBrief) => void
}) {
  const [selectedDomain, setSelectedDomain] = React.useState<CitationDomain | null>(null)

  const gapDomains = domains
    .filter((d) => d.type === 'Editorial' || d.type === 'UGC' || d.type === 'Reference')
    .sort((a, b) => b.times_cited - a.times_cited)

  if (gapDomains.length === 0) {
    return (
      <div className="py-20 text-center">
        <Target className="mx-auto mb-4 h-12 w-12 text-text-muted opacity-40" />
        <p className="mb-2 text-base font-semibold text-text">No gap data yet</p>
        <p className="text-sm text-text-muted">
          Run prompts with Perplexity to collect citation data and reveal gap opportunities.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-5">
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-muted">Gap Analysis</p>
          <h3 className="mb-1.5 text-[15px] font-semibold text-text">
            Sources Perplexity trusts — that you&apos;re not yet featured on
          </h3>
          <p className="text-[13px] text-text-muted">
            These are the authoritative domains AI cites when answering your industry queries. Getting featured on them boosts your AI citation rate.
          </p>
        </div>

        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {gapDomains.map((d) => {
            const hasBrief = !!briefs[d.domain]
            const color = SOURCE_TYPE_COLORS[d.type as keyof typeof SOURCE_TYPE_COLORS] ?? '#6366f1'

            return (
              <div
                key={d.domain}
                onClick={() => setSelectedDomain(d)}
                className="flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-border bg-surface transition-transform duration-150 hover:scale-[0.978]"
              >
                <div className="h-1" style={{ background: `linear-gradient(90deg, ${color}, ${color}44)` }} />
                <div className="flex flex-1 flex-col p-5">
                  <div className="mb-3.5 flex items-center gap-2.5">
                    <FaviconImg domain={d.domain} name={d.domain} size={36} className="!rounded-xl" />
                    <div className="min-w-0 flex-1">
                      <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold text-text">{d.domain}</p>
                      <p className="mt-0.5 text-[11px] text-text-muted">
                        {d.unique_prompts} prompt{d.unique_prompts !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <TypeBadge type={d.type} />
                  </div>

                  <div className="mb-3 rounded-lg border border-border bg-surface-raised px-3 py-2">
                    <span className="text-xs text-text-muted">
                      Cited <span className="font-semibold text-text">{d.times_cited}×</span> across your prompts
                    </span>
                  </div>

                  <div className="mb-3.5">
                    <MentionBadge timesMentioned={d.times_mentioned} timesCited={d.times_cited} />
                  </div>

                  {hasBrief
                    ? (
                      <div className="mb-3.5 flex items-center gap-1.5">
                        <CheckCircle className="h-3 w-3 text-feedback-success" />
                        <span className="text-[11px] text-text-muted">Brief ready</span>
                      </div>
                    )
                    : <div className="mb-3.5 h-[19px]" />
                  }

                  <button
                    className={`mt-auto flex w-full items-center justify-center gap-1.5 rounded-xl py-2.5 text-[13px] font-semibold transition-opacity hover:opacity-90 ${
                      hasBrief
                        ? 'border border-feedback-success/25 bg-feedback-success/10 text-feedback-success'
                        : 'text-white'
                    }`}
                    style={hasBrief ? {} : { background: BRIEF_GRADIENT }}
                  >
                    {hasBrief
                      ? <><CheckCircle className="h-3.5 w-3.5" /> View Brief</>
                      : <><Zap className="h-3.5 w-3.5" /> Get Cited Here — 1 credit</>
                    }
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {selectedDomain && (
        <CitationBriefOverlay
          domain={selectedDomain.domain}
          domainType={selectedDomain.type}
          timesCited={selectedDomain.times_cited}
          prompts={selectedDomain.unique_prompts}
          websiteId={websiteId}
          existingBrief={briefs[selectedDomain.domain] ?? null}
          onClose={() => setSelectedDomain(null)}
          onBriefGenerated={onBriefGenerated}
        />
      )}
    </>
  )
}

// ─── Citations Feed Tab ────────────────────────────────────────────────────────

function CitationsFeedTab({ domains }: { domains: CitationDomain[] }) {
  const sorted = [...domains].sort((a, b) => new Date(b.last_seen).getTime() - new Date(a.last_seen).getTime())

  if (sorted.length === 0) {
    return (
      <div className="py-20 text-center">
        <Globe className="mx-auto mb-4 h-12 w-12 text-text-muted opacity-40" />
        <p className="mb-2 text-base font-semibold text-text">No citations yet</p>
        <p className="mb-5 text-sm text-text-muted">Run prompts with Perplexity to start collecting citations</p>
        <Link
          to="/dashboard/prompts"
          className="inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-[13px] font-semibold text-white"
          style={{ background: BRIEF_GRADIENT }}
        >
          <Search className="h-3.5 w-3.5" /> Run Prompts
        </Link>
      </div>
    )
  }

  const thisWeekDomains = sorted.filter((d) => isThisWeek(d.last_seen))
  const olderDomains = sorted.filter((d) => !isThisWeek(d.last_seen))

  function FeedGroup({ items, label }: { items: CitationDomain[]; label: string }) {
    if (items.length === 0) return null
    return (
      <div>
        <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-widest text-text-muted">{label}</p>
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          {items.map((d, i) => (
            <div
              key={d.domain}
              className={`flex items-center gap-3 px-4 py-3 ${i < items.length - 1 ? 'border-b border-border' : ''}`}
            >
              <FaviconImg domain={d.domain} name={d.domain} size={20} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold text-text">{d.domain}</span>
                  <TypeBadge type={d.type} />
                  <MentionBadge timesMentioned={d.times_mentioned} timesCited={d.times_cited} />
                </div>
                <p className="mt-0.5 text-[11px] text-text-muted">
                  Cited in <strong className="text-text">{d.unique_prompts}</strong> prompt{d.unique_prompts !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-[13px] font-bold text-text">{d.times_cited}×</p>
                <p className="mt-0.5 text-[10px] text-text-muted">{relativeTime(d.last_seen)}</p>
              </div>
              {d.urls[0] && (
                <a
                  href={d.urls[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 text-text-muted transition-colors hover:text-brand-accent"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <FeedGroup items={thisWeekDomains} label="This Week" />
      <FeedGroup items={olderDomains} label="Earlier" />
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export function SourcesPage() {
  const [tab, setTab] = React.useState<PageTab>('landscape')
  const [mentionFilter, setMentionFilter] = React.useState<MentionFilter>('all')
  const queryClient = useQueryClient()

  const { websiteId } = useActiveWebsite()

  const { data: sourcesData, isLoading } = useQuery({
    queryKey: ['sources', websiteId],
    queryFn: () => api.get<SourcesData>(`/api/sources?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const { data: briefsData } = useQuery({
    queryKey: ['citation-intelligence', websiteId],
    queryFn: () =>
      api.get<{ briefs?: CitationBrief[] }>(`/api/citation-intelligence?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const briefs: Record<string, CitationBrief> = React.useMemo(() => {
    const map: Record<string, CitationBrief> = {}
    for (const b of briefsData?.briefs ?? []) map[b.domain] = b
    return map
  }, [briefsData])

  function handleBriefGenerated(brief: CitationBrief) {
    queryClient.setQueryData<{ briefs?: CitationBrief[] }>(
      ['citation-intelligence', websiteId],
      (old) => ({
        briefs: [
          ...(old?.briefs ?? []).filter((b) => b.domain !== brief.domain),
          brief,
        ],
      }),
    )
  }

  const domains = sourcesData?.domains ?? []

  // Gap Analysis only ever shows Editorial/UGC/Reference domains (see
  // GapAnalysisTab's own filter below) — scope the toggle's counts to that
  // same subset while on that tab, so "All (N)" matches what's on screen
  // instead of counting Competitor/Own domains the tab never renders.
  const countScope = React.useMemo(
    () => (tab === 'gaps'
      ? domains.filter((d) => d.type === 'Editorial' || d.type === 'UGC' || d.type === 'Reference')
      : domains),
    [domains, tab],
  )

  const mentionCounts = React.useMemo(() => ({
    all: countScope.length,
    mentioned: countScope.filter((d) => d.times_mentioned > 0).length,
    not_mentioned: countScope.filter((d) => d.times_mentioned === 0).length,
  }), [countScope])

  const filteredDomains = React.useMemo(() => {
    if (mentionFilter === 'mentioned') return domains.filter((d) => d.times_mentioned > 0)
    if (mentionFilter === 'not_mentioned') return domains.filter((d) => d.times_mentioned === 0)
    return domains
  }, [domains, mentionFilter])

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1200px] space-y-5">
        <div>
          <div className="mb-2 h-7 w-64 animate-pulse rounded-lg bg-surface-overlay" />
          <div className="h-4 w-80 animate-pulse rounded-lg bg-surface-overlay" />
        </div>
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-9 w-32 animate-pulse rounded-xl bg-surface-overlay" />)}
        </div>
        <div className="h-48 animate-pulse rounded-xl border border-border bg-surface" />
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl border border-border bg-surface" />)}
        </div>
      </div>
    )
  }

  if (domains.length === 0) {
    return (
      <div className="mx-auto max-w-[1200px] space-y-6">
        <div>
          <div className="mb-1.5 flex items-center gap-2.5">
            <Link2 className="h-5 w-5 text-brand-accent" />
            <h1 className="text-2xl font-bold text-text">Citation Intelligence</h1>
          </div>
          <p className="text-sm text-text-muted">Discover which sources AI trusts — and how to get your brand cited there.</p>
        </div>
        <div className="flex flex-col items-center rounded-2xl border border-border bg-surface py-20 text-center">
          <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-brand-accent/20 bg-brand-accent/10">
            <Link2 className="h-7 w-7 text-brand-accent" />
          </div>
          <p className="mb-2 text-base font-semibold text-text">No citation data yet</p>
          <p className="mb-6 max-w-sm text-[13px] leading-relaxed text-text-muted">
            Run prompts with Perplexity enabled to collect the domains AI cites when discussing your industry.
          </p>
          <Link
            to="/dashboard/prompts"
            className="inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-[13px] font-semibold text-white"
            style={{ background: BRIEF_GRADIENT }}
          >
            <Search className="h-3.5 w-3.5" /> Run Prompts
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-[1200px]">
      {/* Header */}
      <div className="mb-7">
        <div className="mb-1.5 flex items-center gap-2.5">
          <Link2 className="h-5 w-5 text-brand-accent" />
          <h1 className="text-2xl font-bold text-text">Citation Intelligence</h1>
        </div>
        <p className="text-sm text-text-muted">Discover which sources AI trusts — and how to get your brand cited there.</p>
      </div>

      {/* Tab bar */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <TabButton active={tab === 'landscape'} onClick={() => setTab('landscape')} icon={<PieChart className="h-3.5 w-3.5" />} label="Landscape" />
          <TabButton active={tab === 'gaps'} onClick={() => setTab('gaps')} icon={<Target className="h-3.5 w-3.5" />} label="Gap Analysis" />
          <TabButton active={tab === 'feed'} onClick={() => setTab('feed')} icon={<List className="h-3.5 w-3.5" />} label="Citations Feed" />
        </div>
        <MentionFilterToggle value={mentionFilter} onChange={setMentionFilter} counts={mentionCounts} />
      </div>

      {/* Tab content */}
      {tab === 'landscape' && <LandscapeTab domains={filteredDomains} />}
      {tab === 'gaps' && (
        <GapAnalysisTab
          domains={filteredDomains}
          websiteId={websiteId}
          briefs={briefs}
          onBriefGenerated={handleBriefGenerated}
        />
      )}
      {tab === 'feed' && <CitationsFeedTab domains={filteredDomains} />}
    </div>
  )
}
